import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { GoonSession, GoonSessionSummary } from "@gooner/shared";
import { api } from "../lib/api";
import { renderSessionCard } from "./sessionCard";
import { fmtDurationMs, fmtGapMs, humanBytes } from "../lib/statFormat";
import { SessionCardDownload } from "../components/SessionCardDownload";

/**
 * The post-session recap, and the card you share out of it.
 *
 * TWO SURFACES, ONE RENDER. What is on screen is the actual PNG, previewed — not a DOM
 * lookalike beside a canvas nobody sees until it lands in their downloads. The old modal
 * was exactly that: a gold-on-ink Tailwind panel next to a `shareImage()` that painted its
 * title hot pink, so the file you posted did not match the thing you approved. Below the
 * preview is the long tail the card has no room for.
 *
 * Mounted by DisplayApp (the screen that pressed Stop) and by Goontroller (the phone that
 * did). It fetches its own summary from the session id, which is why both can show it.
 */

/** One label/value row of the long tail. */
function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[var(--color-edge)]/60 py-1.5 last:border-0">
      <span className="text-[12px] text-zinc-500">{label}</span>
      <span className="text-[13px] font-semibold text-zinc-200">{value}</span>
    </div>
  );
}

/**
 * Everything the 4:5 card had no room for. The card is the distillation; this is the
 * whole read-out, and it can scroll, so a stat earns its place here by being true rather
 * than by fitting.
 */
function FullRecap({ s }: { s: GoonSessionSummary }) {
  const durationMs = (s.session.endedAt ?? Date.now()) - s.session.startedAt;
  const hours = durationMs / 3_600_000;
  const rows: Array<[string, ReactNode]> = [
    ["Time gooned", fmtDurationMs(durationMs)],
    ["Clips played", s.session.views.toLocaleString()],
    ["Unique clips", s.uniqueClips.toLocaleString()],
    [
      "Repeat rate",
      s.uniqueClips ? `${(s.session.views / s.uniqueClips).toFixed(2)}× per clip` : "—",
    ],
    ["Video on screen (est.)", fmtDurationMs(s.estimatedWatchSec * 1000)],
    ["Data burned", humanBytes(s.session.bytes)],
    ["Edges", s.session.edges.toLocaleString()],
    ["Edges per hour", hours > 0.05 ? (s.session.edges / hours).toFixed(1) : "—"],
  ];
  if (s.msToFirstEdge != null) rows.push(["Time to first edge", fmtGapMs(s.msToFirstEdge)]);
  if (s.longestEdgeGapMs != null) rows.push(["Longest dry spell", fmtGapMs(s.longestEdgeGapMs)]);
  if (s.shortestEdgeGapMs != null) rows.push(["Back-to-back best", fmtGapMs(s.shortestEdgeGapMs)]);
  if (s.peakWindowEdges > 1) rows.push(["Peak 5 minutes", `${s.peakWindowEdges} edges`]);
  if (s.audible + s.silent > 0) {
    rows.push([
      "With sound",
      `${Math.round((s.audible / (s.audible + s.silent)) * 100)}% of clips`,
    ]);
  }
  if (s.portrait + s.landscape > 0) {
    rows.push(["Portrait / landscape", `${s.portrait} / ${s.landscape}`]);
  }
  if (s.signedIn) {
    // "Earned this session" (the watch-time currency) stood here until 2026-09-09:
    // watch-time no longer ties to a session's lifecycle, so there is nothing left for
    // a SINGLE session to report earning — see FapsService.settleWatchThreshold.
    rows.push(["Fapped this session", s.faps.toLocaleString()]);
    rows.push(["Loops pinned", s.loops.toLocaleString()]);
  }
  rows.push(["Session score", `${s.score} / 100 · ${s.title}`]);
  if (s.ranked) {
    rows.push(["Longer than", `${s.duration.pct}% of all sessions`]);
    rows.push(["More edges than", `${s.edges.pct}% of all sessions`]);
    rows.push(["More clips than", `${s.clips.pct}% of all sessions`]);
  }
  if (s.ownTotal > 1) {
    rows.push(["Your own ranking", `#${s.ownRank} longest of ${s.ownTotal}`]);
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-accent-soft)]">
          The whole read-out
        </div>
        <div className="rounded-xl border border-[var(--color-edge)] bg-[var(--color-ink)]/50 px-3 py-1">
          {rows.map(([label, value]) => (
            <Stat key={label} label={label} value={value} />
          ))}
        </div>
      </div>

      {s.facets.length > 0 && (
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-accent-soft)]">
            Tonight&rsquo;s type
          </div>
          <div className="space-y-1.5 rounded-xl border border-[var(--color-edge)] bg-[var(--color-ink)]/50 p-3">
            {s.facets.map((f) => (
              <div key={f.tag} className="flex items-center gap-2">
                <span className="w-28 shrink-0 truncate text-[12px] text-zinc-300">
                  {f.label}
                </span>
                <span className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--color-panel-2)]">
                  <span
                    className="block h-full rounded-full bg-[var(--color-accent)]"
                    style={{ width: `${Math.max(2, Math.round(f.share * 100))}%` }}
                  />
                </span>
                <span className="w-10 shrink-0 text-right text-[12px] tabular-nums text-zinc-500">
                  {Math.round(f.share * 100)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!s.signedIn && (
        <p className="text-center text-[11px] text-zinc-500">
          Signed out — faps and loops aren&rsquo;t counted against this session. Make an
          account and the next card carries them.
        </p>
      )}
    </div>
  );
}

export function SessionSummaryModal({
  session,
  onClose,
  username,
  deviceId,
}: {
  session: GoonSession;
  onClose: () => void;
  /** Stamped on the card when signed in. */
  username?: string;
  /** A screen acts for its device's owner — the same claim /session/stop makes. */
  deviceId?: string;
}) {
  const [summary, setSummary] = useState<GoonSessionSummary | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  // Fetch, then render. Both halves are cancellable: the card render awaits poster decodes
  // (up to 4s each), and without the flag a slow one resolves into an unmounted component.
  // toDataURL is wrapped because a tainted canvas throws SecurityError, and an unhandled
  // one here is a spinner that never resolves and never says why.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await api.session.summary(session.id, deviceId);
        if (cancelled || !r.summary) {
          if (!cancelled) setFailed(true);
          return;
        }
        setSummary(r.summary);
        const canvas = await renderSessionCard(r.summary, username);
        if (cancelled) return;
        setUrl(canvas.toDataURL("image/png"));
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session.id, deviceId, username]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="my-auto w-full max-w-md space-y-4 rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold text-zinc-200">Your goon card</h2>

        {failed ? (
          <div className="flex aspect-[4/5] items-center justify-center rounded-xl bg-[var(--color-ink)] px-6 text-center text-sm text-zinc-400">
            The card couldn&rsquo;t be drawn. Reload and try again.
          </div>
        ) : url ? (
          <img
            src={url}
            alt="Your goon session card"
            className="w-full rounded-xl border border-[var(--color-edge)]"
          />
        ) : (
          <div className="flex aspect-[4/5] items-center justify-center rounded-xl bg-[var(--color-ink)]">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
          </div>
        )}

        <div className="flex gap-2">
          <SessionCardDownload url={url} source="recap" />
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-[var(--color-panel-2)] px-4 py-2.5 text-sm text-zinc-300 hover:bg-[var(--color-edge)]"
          >
            Done
          </button>
        </div>

        {summary && <FullRecap s={summary} />}
      </div>
    </div>
  );
}
